import { _ } from "../../../../core/polyglot/polyglot.js";
import dialog from "../../../../core/widgets/dialog.js";
import FilePermissions from "../../../../models/accounting/document/filePermissions/FilePermissions.js";
import FormBuilder from "../../../../core/widgets/forms/FormBuilder.js";
import SwitchButton from "../../../../core/widgets/SwitchButton/SwitchButton.js";

/**
 * I am the widget used to edit an entry's (file or folder)
 * permissions.  I display a popup to choose access rights for each
 * role access (normal or limited) and a toggle to inherit access
 * rights from the containing folder.
 *
 * @param {editEntryPermissionsCommand} [command] - The command editing the file permissions
 */
export default dialog.subclass((that, my) => {
	my.initialize = function (command) {
		my.super({
			header: _("Permissions"),
			body: my.content,
		});

		my.command = command;

		my.filePermissions = my.command.getEntry().getFilePermissions();
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.inheritAccessRightsButton = new SwitchButton({
			state: my.filePermissions.isInheritingAccessRights(),
			dataTestName: "inherit-access-rights",
		});

		my.inheritAccessRightsButton.stateChanged.register(
			that.update.bind(that),
		);

		my.form = new FormBuilder({
			model: my.filePermissions,
			showActions: false,
			labelClass: "col-sm-3",
			inputClass: "col-sm-9",
		});

		FilePermissions.allRoleAccessNames().forEach((roleName) => {
			const roleNameAccessRight =
				my.filePermissions.getAccessRightForRoleName(roleName);

			const items = FilePermissions.allAccessRights().map(
				(accessRight) => ({
					label: accessRight.getLabel(),
					value: accessRight,
					selected: roleNameAccessRight === accessRight,
				}),
			);

			my.form.newDropDownField({
				accessor: my.filePermissions.getAccessRightForRoleName.bind(
					my.filePermissions,
					roleName,
				),
				mutator: (accessRight) => {
					my.filePermissions.setAccessRightForRoleName(
						roleName,
						accessRight,
					);
				},
				label: FilePermissions.getRoleLabelForRoleName(roleName),
				items,
				name: "access-rights",
			});
		});

		my.footer = (html) => {
			html.div(
				{ klass: "form-actions text-right" },
				html.span(
					html
						.button(
							{
								klass: "btn btn-primary",
								type: "submit",
							},
							_("Set permissions"),
						)
						.click(my.setPermissions),
					" ",
					_("or"),
					" ",
					html.a({ klass: "cancel" }, _("Cancel")).click(that.hide),
				),
			);
		};
	};

	my.content = function (html) {
		html.div(
			{ klass: "section small" },
			html.div(
				{ klass: "col-sm-3 text-right" },
				html.label({ klass: "switch-label" }, _("Inherit access")),
			),
			html.div(
				{ klass: "col-sm-9 text-right" },
				my.inheritAccessRightsButton,
			),
		);

		if (my.inheritAccessRightsButton.isOff()) {
			html.render(my.form);
		} else {
			my.renderInheritedPermissionsOn(html);
		}
	};

	my.renderInheritedPermissionsOn = function (html) {
		const entry = my.command.getEntry();
		const parentFilePermissions = entry.parent().getFilePermissions();

		html.div(
			{ klass: "alert alert-info" },
			html.p(
				_(
					"This {fileOrFolder} does not specify permissions on its own but instead reuses the permissions of the parent folder.",
					{
						fileOrFolder: entry.isFile() ? _("file") : _("folder"),
					},
				),
			),
		);

		html.p(_("The permissions of the parent folder are as follows:"));

		html.dl(
			FilePermissions.allRoleAccessNames().map((roleName) => (html) => {
				html.dt(FilePermissions.getRoleLabelForRoleName(roleName));

				html.dd(
					parentFilePermissions
						.getAccessRightForRoleName(roleName)
						.getLabel(),
				);
			}),
		);
	};

	my.setPermissions = function () {
		that.hide();

		my.form.submit();

		my.filePermissions.setInheritAccess(
			my.inheritAccessRightsButton.isOn(),
		);

		my.command.doPerform();
	};
});
